import { ModelTransformer } from '../../../common/common.model';

import { OaPeopleSort } from './sorting.model';

export interface FractionColours {
    backgroundColour?: string;
    numeratorColour?: string;
    denominatorColour?: string;
}
export class FLOAPerson {
    userId?: number;
    firstName?: string;
    lastName?: string;
    fullName?: string;
    oaScore?: number;
    coachedOpportunities?: number;
    expectedOpportunities?: number;
    uncoachedOpportunities?: number;

    oaThresholdColour?: string;
    rosterSize?: number;
    opportunityCount?: number;
    opportunityInIntervention?: number;
    coachedNonOpportunities?: number;
    missedOpportunities?: number;
    nonKpiCoaching?: number;
    missedOpportunity?: number;
    peopleWithOpportunity?: number;
    totalPeople?: number;
    bottomRole?: boolean;
    fundedCoached?: number;
    fundedUncoached?: number;

    managerFullName?: string;
    managerId?: number;
    daysInSupergroup?: number;
    supergroupId?: number;
    supergroup?: string;
    role?: string;
    roleId?: number;

    // calculated
    percentCoached?: number;
    percentUncoached?: number;

    // for team
    startedLate?: boolean;
    leftEarly?: boolean;
    startDate?: Date;
    endDate?: Date;
    isSubordinate?: boolean;
    isTop?: boolean;

    maxCoached?: number;
    maxUncoached?: number;
    maxExpectedOpportunities?: number;

    // for higer level mgr
    subOrdinates?: FLOAPerson[];
    metrics?: FLOAPerson[];

    isExpanded?: boolean = false;
    isExpandedMetrics?: boolean = false;

    constructor(init?: Partial<FLOAPerson>) {
        Object.assign(this, init);

        const t = Number(this.userId);
        const x = Number(this.oaScore);

        if (typeof t !== 'number' || typeof x !== 'number') {
            console.error('Invalid FLOAPerson.', this);
        }
    }
}

export class FLOAMetric {
    managerId?: number;
    metricId?: number;
    metricInitials?: string;
    metricShortName?: string;
    metricName?: string;
    opportunityCount?: number;
    opportunityInIntervention?: number;
    coachedOpportunities?: number;
    missedOpportunities?: number;

    // calc
    uncoachedOpportunities?: number;
    metricBarMaxWidth?: number;
    topOpps?: number;
}

export class ExtFLOAPerson {
    userId?: number;
    firstName?: string;
    lastName?: string;
    oaScore?: number;
    coachedOpportunities?: number;
    expectedOpportunities?: number;
    uncoachedOpportunities?: number;

    oaThresholdColour?: string;
    rosterSize?: number;
    opportunityCount?: number;
    opportunityInIntervention?: number;
    coachedNonOpportunities?: number;
    missedOpportunities?: number;
    nonKpiCoaching?: number;
    missedOpportunity?: number;
    peopleWithOpportunity?: number;
    totalPeople?: number;
    bottomRole?: number;
    fundedCoached?: number;
    fundedUncoached?: number;

    startedLate?: boolean;
    leftEarly?: boolean;
    startDate?: Date;
    endDate?: Date;
}

export class ExtFLOAPersonTeam {
    userId?: number;
    firstName?: string;
    lastName?: string;
    oaScore?: number;
    coachedOpportunities?: number;
    expectedOpportunities?: number;
    uncoachedOpportunities?: number;

    startedLate?: number;
    leftEarly?: number;
    startDate?: Date;
    endDate?: Date;

    oaThresholdColour?: string;
    opportunityCount?: number;
    opportunityInIntervention?: number;
    coachedNonOpportunities?: number;
    missedOpportunities?: number;
    nonKpiCoaching?: number;
    missedOpportunity?: number;
    peopleWithOpportunity?: number;
    totalPeople?: number;
    bottomRole?: number;
    fundedCoached?: number;
    fundedUncoached?: number;
    managerFirstName?: string;
    managerLastName?: string;
    managerFullName?: string;
    managerId?: number;
    daysInSupergroup?: number;
    supergroupId?: number;
    supergroup?: string;
    role?: string;
    roleId?: number;
}

export class ExtFLOAMetric {
    managerId?: number;
    metricId?: number;
    metricInitials?: string;
    metricShortName?: string;
    metricName?: string;
    opportunityCount?: number;
    opportunityInIntervention?: number;
    coachedOpportunities?: number;
    missedOpportunities?: number;
}

export class FLOATransformer
    implements ModelTransformer<FLOAPerson, ExtFLOAPerson>
{
    transform(model: ExtFLOAPerson): FLOAPerson {
        if (model.userId === null) return null;

        const oaperson = new FLOAPerson({
            ...model,
            oaScore:
                model.oaScore != null ? Math.round(model.oaScore * 100) : null,
            fullName: `${model.firstName} ${model.lastName}`,
            uncoachedOpportunities:
                model.expectedOpportunities - model.coachedOpportunities,
            // eslint-disable-next-line no-unneeded-ternary
            bottomRole: model.bottomRole === 1 ? true : false,
            isTop: true,
        });

        if (oaperson.expectedOpportunities != null) {
            oaperson.percentCoached =
                oaperson.fundedCoached / oaperson.expectedOpportunities;
            oaperson.percentUncoached =
                oaperson.fundedUncoached / oaperson.expectedOpportunities;
        } else {
            oaperson.percentCoached = 0;
            oaperson.percentUncoached = 0;
        }

        return oaperson;
    }
}

export class FLOAMetricTransformer
    implements ModelTransformer<FLOAMetric[], ExtFLOAMetric[]>
{
    transform(model: ExtFLOAMetric[]): FLOAMetric[] {
        const metrics: FLOAMetric[] = [];
        let topOpps: number = 0;

        if (!model || !model.length) {
            console.error('No FLOA Metrics');
            return;
        }

        model.sort(OaPeopleSort.sortFLOAMetrics());

        model.forEach((g) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const f = { ...g } as FLOAMetric;

            // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
            if (g && g.opportunityCount) {
                f.uncoachedOpportunities =
                    g.opportunityCount -
                    (g.coachedOpportunities != null
                        ? g.coachedOpportunities
                        : 0);
            }

            metrics.push(f);
        });

        topOpps = model[0].opportunityCount;

        metrics.forEach((y: FLOAMetric) => {
            y.topOpps = topOpps;

            if (y.opportunityCount != null) {
                y.metricBarMaxWidth = (y.opportunityCount / topOpps) * 100;
            } else {
                y.metricBarMaxWidth = 0;
            }
        });

        return metrics;
    }
}

function flattenObj<T, U = T>(obj: T, key: string): U[] {
    const flat = [];
    // eslint-disable-next-line no-prototype-builtins
    if (obj.hasOwnProperty(key)) {
        // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
        obj[key] &&
            obj[key].forEach((e) => {
                flat.push(e);
                flat.push(...flattenObj(e, key));
            });
    }
    return flat;
}

export class FLOATeamTransformer
    implements ModelTransformer<FLOAPerson[], ExtFLOAPersonTeam[]>
{
    private readonly _leaders: FLOAPerson[];

    constructor(leader?: FLOAPerson) {
        this._leaders = leader ? flattenObj(leader, 'subOrdinates') : [];
        leader && this._leaders.unshift(leader);
    }

    transform(model: ExtFLOAPersonTeam[]): FLOAPerson[] {
        const floa: FLOAPerson[] = [];

        model.forEach((g) => {
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            const s = {
                ...g,
                oaScore: g.oaScore != null ? Math.round(g.oaScore * 100) : null,
                fullName: `${g.firstName} ${g.lastName}`,
                uncoachedOpportunities:
                    g.expectedOpportunities - g.coachedOpportunities,
                // eslint-disable-next-line no-unneeded-ternary
                bottomRole: g.bottomRole === 1 ? true : false,
                // eslint-disable-next-line no-unneeded-ternary
                startedLate: g.startedLate === 1 ? true : false,
                // eslint-disable-next-line no-unneeded-ternary
                leftEarly: g.leftEarly === 1 ? true : false,
                isSubordinate: true,
                isExpanded: false,
                isExpandedMetrics: false,
                isShowingExpandMenu: false,
                isTop: false,
                managerFullName: `${g.managerFirstName} ${g.managerLastName}`,
            } as FLOAPerson;

            // percentage relative to manager's Funded Opportunities
            const leader = this._leaders.find((e) => e.userId === s.managerId);
            if (leader) {
                s.percentCoached = s.fundedCoached / leader.fundedCoached;
                s.percentUncoached = s.fundedUncoached / leader.fundedUncoached;

                s.maxCoached = leader.fundedCoached;
                s.maxUncoached = leader.fundedUncoached;
                s.maxExpectedOpportunities = leader.expectedOpportunities;
            }

            floa.push(s);
        });

        return floa;
    }
}
